import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { TabsBarTab } from '@e3dc-react/shell/Elements/Tabs/TabsBar'
import { useHeaderTabs } from '@e3dc-react/shell/Libs/Header'
import { useEffect, useLayoutEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { SYSTEM_PROTOCOL_TAB_PATH, getSystemProtocolUrl } from '../routes'

/**
 * Tabs for the system status page. Sets them in the header and marks the tab matching the current url as active.
 * The `componentHref` of each tab is the relative route path the page has to render the tab content under.
 */
export const useSystemStatusTabs = (installationId?: string, storageId?: string): TabsBarTab[] => {
  const { pathname } = useLocation()
  const { setTabs, setActiveTab } = useHeaderTabs()
  const { t, selectedAppLanguage } = useCustomTranslation()

  const tabs: TabsBarTab[] = useMemo(
    () =>
      installationId
        ? [
            {
              label: t('dashboard.systemProtocol.title'),
              href: getSystemProtocolUrl(installationId, storageId),
              componentHref: SYSTEM_PROTOCOL_TAB_PATH,
            },
          ]
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedAppLanguage, installationId, storageId]
  )

  useEffect(() => {
    const activeTabIndex = tabs.findIndex(({ href }) => href && (pathname === href || pathname.startsWith(`${href}/`)))
    setTabs(tabs)
    setActiveTab(activeTabIndex === -1 ? 0 : activeTabIndex)
  }, [tabs, pathname, setTabs, setActiveTab])

  // layout effect: clears the tabs in the same commit that unmounts the page, otherwise they stay visible on the next page for a frame
  useLayoutEffect(() => () => setTabs([]), [setTabs])

  return tabs
}
